import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { PrimeReactProvider } from 'primereact/api';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import Layout from './components/Layout';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import ProjectFolders from './components/ProjectFolders';
import Projects from './components/Projects';
import ProjectDetail from './components/ProjectDetail';
import ITTickets from './components/ITTickets';
import ITTicketDetail from './components/ITTicketDetail';
import TaskNotifications from './components/TaskNotifications';
import ProductionTimeEntries from './components/ProductionTimeEntries';
import ProductionTimeReports from './components/ProductionTimeReports';
import ProductionStates from './components/ProductionStates';
import FinanceCompanies from './components/FinanceCompanies';
import FinanceBookTypes from './components/FinanceBookTypes';
import FinanceBooks from './components/FinanceBooks';
import FinanceBookDocuments from './components/FinanceBookDocuments';
import Workers from './components/Workers';
import WorkerDetails from './components/WorkerDetails';
import Positions from './components/Positions';
import AttendanceDaily from './components/AttendanceDaily';
import AbsenceTypes from './components/AbsenceTypes';
import Holidays from './components/Holidays';
import AttendanceReports from './components/AttendanceReports';
import Materials from './components/Materials';
import Receipts from './components/Receipts';
import Disposals from './components/Disposals';
import Resins from './components/Resins';
import Consumption from './components/Consumption';
import LogisticsReports from './components/LogisticsReports';
import ProcurementRequests from './components/ProcurementRequests';
import LogisticsCategories from './components/LogisticsCategories';
import LogisticsGoods from './components/LogisticsGoods';
import ProcurementStatistics from './components/ProcurementStatistics';
import ProcurementItemsTable from './components/ProcurementItemsTable';
import SalesPriceLists from './components/SalesPriceLists';
import MagacinProducts from './components/MagacinProducts';
import MagacinProductCreate from './components/MagacinProductCreate';
import MagacinRemovals from './components/MagacinRemovals';
import MagacinLoans from './components/MagacinLoans';
import MagacinProductTypes from './components/MagacinProductTypes';
import MagacinArrivals from './components/MagacinArrivals';
import MagacinReceivings from './components/MagacinReceivings';
import MagacinReceivingCreate from './components/MagacinReceivingCreate';
import LagerCompanies from './components/LagerCompanies';
import LagerFinishedProducts from './components/LagerFinishedProducts';
import LagerTransport from './components/LagerTransport';
import LagerProduction from './components/LagerProduction';
import LagerCurrentStatus from './components/LagerCurrentStatus';
import LagerReports from './components/LagerReports';
import LagerStorage from './components/LagerStorage';
import StanjeRobeBrands from './components/StanjeRobeBrands';
import StanjeRobeProducts from './components/StanjeRobeProducts';
import StanjeRobeCustomers from './components/StanjeRobeCustomers';
import StanjeRobeStockIn from './components/StanjeRobeStockIn';
import StanjeRobeStockOut from './components/StanjeRobeStockOut';
import StanjeRobeCurrentStatus from './components/StanjeRobeCurrentStatus';
import StanjeRobeCustomerShipments from './components/StanjeRobeCustomerShipments';
import StanjeRobeReports from './components/StanjeRobeReports';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './index.css';
import 'primeflex/primeflex.css';

import HiltiWorkers from './components/HiltiWorkers';
import HiltiProducts from './components/HiltiProducts';
import HiltiMaterialReceipts from './components/HiltiMaterialReceipts';
import HiltiMaterialDisposals from './components/HiltiMaterialDisposals';
import HiltiMaterials from './components/HiltiMaterials';
import HiltiProduction from './components/HiltiProduction';
import HiltiReports from './components/HiltiReports';
import ITEquipment from './components/ITEquipment';
import ITEquipmentCategories from './components/ITEquipmentCategories';
import ITServers from './components/ITServers';
import ITComputers from './components/ITComputers';
import ITSubnetCalculator from './components/ITSubnetCalculator';
import ITIPAddresses from './components/ITIPAddresses';
import ITDevices from './components/ITDevices';
import ITVlanManagement from './components/ITVlanManagement';
import ITPortConfig from './components/ITPortConfig';
import ITIPOverview from './components/ITIPOverview';
import ITIncidents from './components/ITIncidents';
import Users from './components/Users';
import NotificationSettings from './components/NotificationSettings';
import ActivityLogs from './components/ActivityLogs';
import { useAuth } from './contexts/AuthContext';

const HomeRedirect: React.FC = () => {
  const { user } = useAuth();
  if (user?.role === 'customer') {
    return <Navigate to="/stanje-robe/stanje" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

const PlaceholderComponent: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ padding: '2rem', textAlign: 'center' }}>
    <h2>{title}</h2>
    <p>Komponenta u izradi...</p>
  </div>
);

function App() {
  return (
    <PrimeReactProvider value={{ locale: 'sr' }}>
      <AuthProvider>
        <LanguageProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<HomeRedirect />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="notifications" element={<TaskNotifications />} />
              
              {/* Task Management */}
              <Route path="projects" element={<Projects />} />
              <Route path="projects/folder/:folderSlug" element={<Projects />} />
              <Route path="projects/:projectId" element={<ProjectDetail />} />
              <Route path="project-folders" element={<ProjectFolders />} />
              <Route path="it-tickets" element={<ITTickets />} />
              <Route path="it-tickets/:id" element={<ITTicketDetail />} />
              <Route path="my-it-tickets" element={<ITTickets />} />
              
              {/* Evidencija prisustva */}
              <Route path="workers" element={<Workers />} />
              <Route path="workers/:id" element={<WorkerDetails />} />
              <Route path="positions" element={<Positions />} />
              <Route path="attendance" element={<AttendanceDaily />} />
              <Route path="absence" element={<AbsenceTypes />} />
              <Route path="holidays" element={<Holidays />} />
              <Route path="attendance/reports" element={<AttendanceReports />} />
              
              {/* Delovodnik */}
              <Route path="finance/books" element={<FinanceBooks />} />
              <Route path="finance/books/:id" element={<FinanceBookDocuments />} />
              <Route path="finance/book-types" element={<FinanceBookTypes />} />
              <Route path="finance/companies" element={<FinanceCompanies />} />
              
              {/* Vreme Proizvodnje */}
              <Route path="production-time" element={<ProductionTimeEntries />} />
              <Route path="production-time/reports" element={<ProductionTimeReports />} />
              <Route path="production-time/states" element={<ProductionStates />} />
              
              {/* Logistika */}
              <Route path="consumption" element={<Consumption />} />
              <Route path="materials" element={<Materials />} />
              <Route path="receipts" element={<Receipts />} />
              <Route path="disposals" element={<Disposals />} />
              <Route path="resins" element={<Resins />} />
              <Route path="logistics/reports" element={<LogisticsReports />} />
              
              {/* Nabavka i ponude */}
              <Route path="procurement/create" element={<ProcurementRequests />} />
              <Route path="procurement" element={<ProcurementRequests />} />
              <Route path="logistics/goods" element={<LogisticsGoods />} />
              <Route path="logistics/categories" element={<LogisticsCategories />} />
              <Route path="procurement/statistics" element={<ProcurementStatistics />} />
              <Route path="procurement/items" element={<ProcurementItemsTable />} />
              <Route path="sales/price-lists" element={<SalesPriceLists />} />
              
              {/* Magacin */}
              <Route path="magacin/products" element={<MagacinProducts />} />
              <Route path="magacin/products/create" element={<MagacinProductCreate />} />
              <Route path="magacin/removals" element={<MagacinRemovals />} />
              <Route path="magacin/loans" element={<MagacinLoans />} />
              <Route path="magacin/product-types" element={<MagacinProductTypes />} />
              <Route path="magacin/arrivals" element={<MagacinArrivals />} />
              <Route path="magacin/receivings" element={<MagacinReceivings />} />
              <Route path="magacin/receivings/create" element={<MagacinReceivingCreate />} />
              <Route path="logistika/receivings" element={<MagacinReceivings />} />
              <Route path="logistika/receivings/create" element={<MagacinReceivingCreate />} />
              
              {/* Lager gotovih proizvoda */}
              <Route path="lager/reports" element={<LagerReports />} />
              <Route path="lager/production" element={<LagerProduction />} />
              <Route path="lager/transport" element={<LagerTransport />} />
              <Route path="lager/finished-products" element={<LagerFinishedProducts />} />
              <Route path="lager/companies" element={<LagerCompanies />} />
              <Route path="lager/storage" element={<LagerStorage />} />
              <Route path="lager/current-status" element={<LagerCurrentStatus />} />

              {/* Stanje robe */}
              <Route path="stanje-robe/brendovi" element={<StanjeRobeBrands />} />
              <Route path="stanje-robe/roba" element={<StanjeRobeProducts />} />
              <Route path="stanje-robe/kupci" element={<StanjeRobeCustomers />} />
              <Route path="stanje-robe/ulaz" element={<StanjeRobeStockIn />} />
              <Route path="stanje-robe/izlaz" element={<StanjeRobeStockOut />} />
              <Route path="stanje-robe/stanje" element={<StanjeRobeCurrentStatus />} />
              <Route path="stanje-robe/izvestaji" element={<StanjeRobeReports />} />
              <Route path="stanje-robe/isporuke" element={<StanjeRobeCustomerShipments />} />
              
              {/* Finansijski izvestaj gotovog proizvoda */}
              <Route path="financial/finished-products" element={<PlaceholderComponent title="Proizvodi" />} />
              <Route path="financial/production-list" element={<PlaceholderComponent title="Finansijska lista proizvoda" />} />
              
              {/* Hilti */}
              <Route path="hilti/production" element={<HiltiProduction />} />
              <Route path="hilti/workers" element={<HiltiWorkers />} />
              <Route path="hilti/products" element={<HiltiProducts />} />
              <Route path="hilti/materials" element={<HiltiMaterials />} />
              {/* Hilti Magacin Materijala – trenutno koristi istu komponentu kao HiltiMaterials */}
              <Route path="hilti/materials/warehouse" element={<HiltiMaterials />} />
              <Route path="hilti/materials/receipts" element={<HiltiMaterialReceipts />} />
              <Route path="hilti/materials/disposals" element={<HiltiMaterialDisposals />} />
              <Route path="hilti/reports" element={<HiltiReports />} />
              
              {/* IT Upravljanje */}
              <Route path="it/equipment" element={<ITEquipment />} />
              <Route path="it/equipment-categories" element={<ITEquipmentCategories />} />
              <Route path="it/network" element={<PlaceholderComponent title="Mreža" />} />
              <Route path="it/servers" element={<ITServers />} />
              <Route path="it/computers" element={<ITComputers />} />
              <Route path="it/users" element={<Users />} />
              <Route path="it/network-overview" element={<PlaceholderComponent title="Pregled mreže" />} />
              <Route path="it/subnets" element={<PlaceholderComponent title="Subnetovi" />} />
              <Route path="it/ip-assignment" element={<ITIPAddresses />} />
              <Route path="it/devices" element={<ITDevices />} />
              <Route path="it/subnet-calculator" element={<ITSubnetCalculator />} />
              <Route path="it/vlan-management" element={<ITVlanManagement />} />
              <Route path="it/port-config" element={<ITPortConfig />} />
              <Route path="it/ip-overview" element={<ITIPOverview />} />
              <Route path="it/incidents" element={<ITIncidents />} />
              <Route path="it/tickets" element={<PlaceholderComponent title="IT Tiketi" />} />
              
              {/* Administracija */}
              <Route path="admin/activity-logs" element={<ActivityLogs />} />
              <Route path="users" element={<Users />} />
              <Route path="admin/notifications" element={<NotificationSettings />} />
            </Route>
          </Routes>
        </Router>
        </LanguageProvider>
      </AuthProvider>
    </PrimeReactProvider>
  );
}

export default App;
